import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, fmtMoney, fmtNum } from "@/lib/api";
import { PageHeader, Panel, LoadingState, ErrorState } from "@/components/common";
import { DemoBadge } from "@/components/DemoBadge";
import { CandleChart } from "@/components/CandleChart";
import { ArrowLeft, Eye } from "lucide-react";
import { cn } from "@/lib/utils";

const TIMEFRAMES = ["1m", "5m", "15m", "60m", "75m", "125m", "240m", "1D", "1W", "1M"];

export default function InstrumentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tf, setTf] = useState("1D");

  const { data: inst, isLoading, isError, refetch } = useQuery({
    queryKey: ["instrument", id],
    queryFn: async () => (await api.get(`/instruments/${id}`)).data,
  });

  const { data: chart } = useQuery({
    queryKey: ["candles", inst?.symbol, tf],
    queryFn: async () => (await api.get("/candles", { params: { symbol: inst.symbol, timeframe: tf, limit: 160 } })).data,
    enabled: !!inst?.symbol,
  });

  if (isLoading) return <LoadingState />;
  if (isError) return <ErrorState onRetry={refetch} />;

  const up = Number(inst.change) >= 0;
  const stats = [
    ["Instrument ID", inst.instrument_id || inst.symbol], ["Exchange", inst.exchange],
    ["Asset Class", inst.asset_class], ["Subtype", inst.subtype],
    ["Currency", inst.currency], ["Prev Close", fmtMoney(inst.prev_close, inst.currency)],
    ["Volume", fmtNum(inst.volume, 0)],
    ["Tick Size", inst.tick_size ?? "—"], ["Lot Size", inst.lot_size ?? "—"],
    ...(inst.expiry ? [["Expiry", inst.expiry]] : []),
    ...(inst.strike ? [["Strike", fmtMoney(inst.strike, inst.currency)]] : []),
    ...(inst.option_type ? [["Option Type", inst.option_type]] : []),
    ...(inst.underlying ? [["Underlying", inst.underlying]] : []),
    ["Contract Status", (inst.contract_status || "active").toUpperCase()],
    ["Data Quality", (inst.data_quality || "synthetic").toUpperCase()],
    ["Data Status", inst.data_status],
    ["Price Timestamp", new Date(inst.timestamp).toLocaleString("en-IN")],
  ];

  return (
    <div>
      <button
        data-testid="back-to-market"
        onClick={() => navigate("/app/market-watch")}
        className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-200 mb-3"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Market Watch
      </button>

      <PageHeader
        title={inst.symbol}
        subtitle={inst.name}
        icon={Eye}
        testId="instrument-detail-header"
        actions={
          <div className="text-right">
            {inst.contract_status === "expired" && (
              <span data-testid="expired-badge" className="inline-block mb-1 text-[10px] font-mono px-1.5 py-0.5 rounded border uppercase text-rose-400 border-rose-500/30 bg-rose-500/10">Expired · Not Tradable</span>
            )}
            <div className="font-mono-num text-2xl font-bold text-slate-100">{fmtMoney(inst.last_price, inst.currency)}</div>
            <div className={cn("font-mono-num text-sm font-semibold", up ? "text-emerald-400" : "text-rose-400")}>
              {up ? "▲ +" : "▼ "}{fmtMoney(inst.change, inst.currency)} ({up ? "+" : ""}{inst.change_pct}%)
            </div>
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <Panel
          className="lg:col-span-3"
          testId="instrument-chart-panel"
          title="Price Chart"
          actions={
            <div className="flex items-center gap-2">
              <DemoBadge />
              <div className="flex rounded-md border border-[var(--border-subtle)] overflow-hidden">
                {TIMEFRAMES.map((t) => (
                  <button
                    key={t}
                    data-testid={`tf-${t}`}
                    onClick={() => setTf(t)}
                    className={cn("px-2.5 py-1 text-[11px] font-mono-num transition-colors", tf === t ? "bg-blue-600 text-white" : "text-slate-400 hover:bg-[var(--card-hover)]")}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
          }
        >
          <div className="p-2">
            {chart ? <CandleChart candles={chart.candles} zones={chart.zones} height={420} /> : <LoadingState label="Loading candles..." />}
          </div>
        </Panel>

        <Panel title="Instrument Details" testId="instrument-stats-panel">
          <div className="divide-y divide-[var(--border-subtle)]">
            {stats.map(([k, v]) => (
              <div key={k} className="flex items-center justify-between px-4 py-2.5">
                <span className="text-[11px] text-slate-500">{k}</span>
                <span className="text-xs font-mono-num text-slate-200">{v}</span>
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}
